import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

/* Derived classification: map each answer choice to a reporting label (e.g. Brand Usage → New User). */

export const CLASSIFIABLE_TYPES = new Set(['single_choice', 'button_choice', 'dropdown', 'yes_no']);

const optionLabels = (q) => {
  const raw = Array.isArray(q.options) && q.options.length ? q.options
    : q.type === 'yes_no' ? [{ label: 'Yes' }, { label: 'No' }] : [];
  return raw.map((o) => (typeof o === 'string' ? o : o?.label || '')).map((s) => s.trim()).filter(Boolean);
};

function MappingRow({ option, label, onSave, index }) {
  const [draft, setDraft] = useState(label);
  useEffect(() => { setDraft(label); }, [label]);
  return (
    <div className="space-y-1" data-testid={`classification-map-row-${index}`}>
      <p className="text-xs text-[#0F172A] leading-snug break-words">{option}</p>
      <Input value={draft} maxLength={60} onChange={(e) => setDraft(e.target.value)} onBlur={() => draft.trim() !== label && onSave(draft.trim())}
        placeholder="Label, e.g. New User" className="text-xs h-8" data-testid={`classification-map-label-${index}`} />
    </div>
  );
}

export default function ClassificationSettings({ question, onUpdate }) {
  const cs = question.classification_settings || {};
  const maps = cs.mappings || [];
  const options = optionLabels(question);
  const [name, setName] = useState(cs.name || '');
  useEffect(() => { setName(cs.name || ''); }, [cs.name]);
  const save = (patch) => onUpdate(question.id, { classification_settings: { enabled: !!cs.enabled, name: cs.name || '', mappings: maps, ...patch } });
  const labelFor = (opt) => maps.find((m) => m.option.toLowerCase() === opt.toLowerCase())?.label || '';
  const setLabel = (opt, label) => save({ mappings: [...maps.filter((m) => m.option.toLowerCase() !== opt.toLowerCase()), ...(label ? [{ option: opt, label }] : [])] });
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="classification-settings">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-xs font-bold text-[#64748B] uppercase">Classify answers</h4>
        <Switch checked={!!cs.enabled} onCheckedChange={(v) => save({ enabled: v, name: cs.name || name.trim() || 'Classification' })} data-testid="classification-enabled-toggle" />
      </div>
      <p className="text-[10px] text-[#94A3B8]">Turn answers into a reporting label shown on Response Detail (e.g. “First time” → New User). Answers are never changed.</p>
      {cs.enabled && (
        <>
          <div>
            <label className="text-xs font-medium text-[#64748B] block mb-1">Classification name</label>
            <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name.trim() !== cs.name && save({ name: name.trim() })}
              placeholder="e.g. Brand Usage" className="text-sm" data-testid="classification-name-input" />
          </div>
          <div className="space-y-3">
            {options.length === 0 && <p className="text-[10px] text-amber-700">Add answer choices first.</p>}
            {options.map((opt, i) => <MappingRow key={opt} index={i} option={opt} label={labelFor(opt)} onSave={(l) => setLabel(opt, l)} />)}
          </div>
        </>
      )}
    </div>
  );
}
